/**
 * Utilitaire de formatage des numéros de téléphone
 *
 * Utilise libphonenumber-js pour formater les numéros internationaux
 * avec les nouveaux champs phonePrefix et phoneNumber.
 *
 * @module formatPhoneNumber
 * @author Équipe Brydge
 * @since 2.0.0
 */

import {parsePhoneNumber} from 'libphonenumber-js';

/**
 * Formate un numéro de téléphone pour l'affichage
 *
 * Reconstruit et formate un numéro depuis phonePrefix et phoneNumber
 * au format international lisible (ex: "+32 475 12 34 56")
 *
 * @param {string} phonePrefix - Code pays (ex: "+32", "+33")
 * @param {string} phoneNumber - Numéro national nettoyé
 * @return {string} Numéro formaté ou chaîne vide si invalide
 *
 * @example
 * formatPhoneNumber("+32", "475123456") // "+32 475 12 34 56"
 * formatPhoneNumber("+33", "750467191") // "+33 7 50 46 71 91"
 * formatPhoneNumber("", "")             // ""
 */
export const formatPhoneNumber = (phonePrefix, phoneNumber) => {
  if (!phonePrefix || !phoneNumber) {
    return '';
  }

  try {
    // Construire le numéro complet au format E.164
    const e164 = `${phonePrefix}${phoneNumber}`;

    // Parser et formater avec libphonenumber-js
    const parsed = parsePhoneNumber(e164);
    if (!parsed) {
      return e164; // Fallback sur format brut si parsing échoue
    }

    // Retourner le format international lisible
    return parsed.formatInternational();
  } catch (error) {
    // En cas d'erreur, retourner le format brut
    return `${phonePrefix}${phoneNumber}`;
  }
};

/**
 * Extrait phonePrefix et phoneNumber depuis un objet customer/reservation/voucher
 *
 * Gère automatiquement les cas où les champs peuvent être dans customer,
 * customerId (réservations populées), ou purchasedBy (bons cadeaux).
 *
 * @param {Object} data - Objet contenant les données client
 * @param {string} [data.phonePrefix] - Préfixe direct
 * @param {string} [data.phoneNumber] - Numéro direct
 * @param {Object} [data.customer] - Objet customer imbriqué
 * @param {Object} [data.customerId] - Objet customerId imbriqué
 * @param {Object} [data.purchasedBy] - Objet purchasedBy imbriqué (gift vouchers)
 * @param {string} [data.phone] - Numéro plat déjà saisi/formaté (formulaires)
 * @return {string} Numéro formaté ou chaîne vide
 *
 * @example
 * // Cas 1: Champs directs
 * getFormattedPhone({phonePrefix: "+32", phoneNumber: "475123456"})
 * // Returns: "+32 475 12 34 56"
 *
 * // Cas 2: Dans customer
 * getFormattedPhone({customer: {phonePrefix: "+32", phoneNumber: "475123456"}})
 * // Returns: "+32 475 12 34 56"
 *
 * // Cas 3: Dans customerId (reservation)
 * getFormattedPhone({customerId: {phonePrefix: "+32", phoneNumber: "475123456"}})
 * // Returns: "+32 475 12 34 56"
 *
 * // Cas 4: Dans purchasedBy (gift voucher)
 * getFormattedPhone({purchasedBy: {phonePrefix: "+32", phoneNumber: "475123456"}})
 * // Returns: "+32 475 12 34 56"
 */
export const getFormattedPhone = (data) => {
  if (!data) return '';

  // Cas 1: Champs directs
  if (data.phonePrefix && data.phoneNumber) {
    return formatPhoneNumber(data.phonePrefix, data.phoneNumber);
  }

  // Cas 2: Dans customer
  if (data.customer?.phonePrefix && data.customer?.phoneNumber) {
    return formatPhoneNumber(data.customer.phonePrefix, data.customer.phoneNumber);
  }

  // Cas 3: Dans customerId (réservations)
  if (data.customerId?.phonePrefix && data.customerId?.phoneNumber) {
    return formatPhoneNumber(data.customerId.phonePrefix, data.customerId.phoneNumber);
  }

  // Cas 4: Dans purchasedBy (gift vouchers)
  if (data.purchasedBy?.phonePrefix && data.purchasedBy?.phoneNumber) {
    return formatPhoneNumber(data.purchasedBy.phonePrefix, data.purchasedBy.phoneNumber);
  }

  // Cas 5 (repli) : champ plat `phone`, typiquement une valeur de formulaire
  // déjà saisie ou pré-formatée (CustomerStep, NewCustomerForm). Restitué tel
  // quel — sans ce repli, un objet sans phonePrefix/phoneNumber masquerait un
  // numéro pourtant disponible.
  if (typeof data.phone === 'string' && data.phone.trim()) {
    return data.phone.trim();
  }

  return '';
};

export default formatPhoneNumber;
